import type { Element, Root, Text } from "hast";
import { findEvidenceTokens } from "./recognizers";

// Never tokenise inside these: <code>/<pre> carry the pmc-figure/pmc-table fenced directives whose
// body must stay a single string child (the `code` renderer checks `typeof children === "string"`),
// and script/style content is not prose.
const SKIP_TAGS = new Set(["code", "pre", "script", "style"]);

/**
 * Rehype plugin that wraps recognised quantitative-evidence tokens (n=, p-values, effect sizes,
 * confidence intervals) in `<span data-evidence-token="{slug}">` markers, driven by the recognizer
 * register.
 *
 * Runs on the parsed HAST (after rehypeRaw), so it never touches Markdown syntax, fenced
 * directives, tables, or the injected evidence `<mark>`. Only the slug is emitted; the
 * MarkdownWithHighlight `span` renderer resolves it to colour and tooltip via the register.
 */
export function rehypeEvidenceTokens(): (tree: Root) => void {
  return (tree: Root): void => {
    transformChildren(tree);
  };
}

function transformChildren<Parent extends Root | Element>(parent: Parent): void {
  const out: Parent["children"][number][] = [];
  for (const child of parent.children) {
    if (child.type === "text") {
      for (const piece of splitTextNode(child)) {
        out.push(piece as Parent["children"][number]);
      }
      continue;
    }
    if (child.type === "element" && !SKIP_TAGS.has(child.tagName)) {
      transformChildren(child);
    }
    out.push(child);
  }
  parent.children = out as Parent["children"];
}

/**
 * Splits a text node around its evidence tokens, returning the original node untouched when there
 * are none. Matched runs become `<span>` elements; the surrounding text stays as text nodes.
 */
function splitTextNode(node: Text): (Text | Element)[] {
  const matches = findEvidenceTokens(node.value);
  if (matches.length === 0) return [node];

  const pieces: (Text | Element)[] = [];
  let cursor = 0;
  for (const { start, end, recognizer } of matches) {
    if (start > cursor) {
      pieces.push({ type: "text", value: node.value.slice(cursor, start) });
    }
    pieces.push({
      type: "element",
      tagName: "span",
      properties: { dataEvidenceToken: recognizer.slug },
      children: [{ type: "text", value: node.value.slice(start, end) }],
    });
    cursor = end;
  }
  if (cursor < node.value.length) {
    pieces.push({ type: "text", value: node.value.slice(cursor) });
  }
  return pieces;
}
